import { Callout, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionProperties, EnvironmentV2Resource, GitRefResource, ProcessTemplateVersionSummary, PropertyValueResource, VariableSetResource } from "@octopusdeploy/octopus-server-client";
import { generateSlug } from "@octopusdeploy/utilities";
import { isEmpty, isEqual } from "lodash";
import * as React from "react";
import { useState } from "react";
import { BlueprintActionDetails } from "~/areas/projects/components/Process/Blueprints/BlueprintActionDetails";
import { UpdateProcessTemplateVersionCallout } from "~/areas/projects/components/Process/Blueprints/ProcessTemplateVersionCallout";
import { getVersionMask, mapMaskToUpdateBehaviour, mapMaskToUsageConstraint, ProcessTemplateVersionSelector, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Process/Blueprints/ProcessTemplateVersionSelector";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/Blueprints/processTemplateId";
import { deleteActionAndRedirect, getDeleteProcessMenuItem, isMatchingProcessTemplate, processScopedEditPermission } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { ErrorsForAction } from "~/areas/projects/components/Process/Common/ErrorsForAction";
import StepName from "~/areas/projects/components/Process/Common/StepName";
import { useProcessBlueprintsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessBlueprintsContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import type { FailedPermissionCheck, ProcessStepActionState } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import { ProcessSubPageLayout } from "~/areas/projects/components/Process/ProcessSubPageLayout";
import styles from "~/areas/projects/components/Process/style.module.less";
import type { StoredAction, StoredStep } from "~/areas/projects/components/Process/types";
import { isDeploymentOrRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/AllVariables";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import ActionList from "~/components/ActionList/index";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { ExpandableContainer } from "~/components/Expandable/index";
import Logo from "~/components/Logo/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { ExpandableFormSection, required, Summary, UnstructuredFormSection } from "~/components/form/index";
import useLocalStorage from "~/hooks/useLocalStorage";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
import { DebounceText } from "~/primitiveComponents/form/Text/Text";
interface ProcessBlueprintActionDetailsProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    isNew: boolean;
    cleanAction: StoredAction | null;
    doBusyTask: DoBusyTask;
    gitRefResource: GitRefResource | undefined;
    releaseCreationPackageStepId: string | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentV2Resource[];
}
export function ProcessBlueprintStepDetails({ step, action, stepOther, isNew, cleanAction, doBusyTask, gitRefResource, releaseCreationPackageStepId, processVariableSet, libraryVariableSets, environments }: ProcessBlueprintActionDetailsProps) {
    const processContext = useProcessContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processTemplateContext = useProcessBlueprintsFromContext();
    const processTemplateSlug = getProcessTemplateSlugFromAction(action);
    const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
    const actionErrors = action ? processErrorSelectors.getActionErrors(action.Id, processContext.selectors) : [];
    const [initialActionProperties, setInitialActionProperties] = useState(action.Properties);
    const [initialCleanModel, setInitialCleanModel] = useState(processContext.state.cleanModel);
    if (!isEqual(initialCleanModel, processContext.state.cleanModel)) {
        setInitialCleanModel(processContext.state.cleanModel);
        setInitialActionProperties(action.Properties);
    }
    const selectedProcessTemplateDetails = processTemplateContext.find((pt) => isMatchingProcessTemplate(pt, processTemplateSlug, processTemplateVersionMask));
    const selectedProcessTemplate = selectedProcessTemplateDetails?.processTemplate;
    const [initialProcessTemplateVersion, setInitialProcessTemplateVersion] = useState<string | undefined | null>(selectedProcessTemplateDetails?.processTemplateVersion);
    const getFieldError = (value: string) => {
        return processErrorSelectors.getActionFieldError(action.Id, processContext.selectors, value);
    };
    const setActionMetaProperties: <K extends keyof StoredAction>(state: Pick<StoredAction, K> | StoredAction, callback?: () => void) => void = (state, callback) => {
        processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, ...state }));
        callback && callback();
    };
    const setName = (value: string) => {
        setActionMetaProperties({ Name: value });
    };
    const setSlug = (value: string) => {
        setActionMetaProperties({ Slug: value });
    };
    const setDisabled = (value: boolean) => {
        setActionMetaProperties({ IsDisabled: value });
    };
    const setActionProperties = (properties: Partial<ActionProperties>, callback?: () => void) => {
        processContext.actions.setActionProperties(action.Id, properties);
        callback && callback();
    };
    const setVersionMaskProperty = (versionMask: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.VersionMask": versionMask,
        };
        setActionProperties(properties);
    };
    const setProcessTemplateSlugProperty = (slug: string) => {
        const properties: ActionProperties = {
            "Octopus.Action.ProcessTemplate.Reference.Slug": slug,
        };
        setActionProperties(properties);
    };
    const setParameterValue = (parameterName: string, value: PropertyValueResource | undefined) => {
        if (value) {
            const properties: ActionProperties = { [parameterName]: value };
            setActionProperties(properties);
        }
        else {
            processContext.actions.removeActionProperties(action.Id, [parameterName]);
        }
    };
    const { result: distinctTemplates } = useQuery((repository) => repository.ProcessTemplates.summaries(), [], "Get Templates");
    const [selectedProcessTemplateOption, setSelectedProcessTemplateOption] = useState<string | undefined>(isEmpty(processTemplateSlug) ? undefined : processTemplateSlug);
    const { result: latestProcessTemplateVersionSummary } = useQuery(async (repository) => {
        let result: ProcessTemplateVersionSummary | undefined = undefined;
        if (selectedProcessTemplateOption) {
            result = await repository.ProcessTemplates.getLatestVersionSummary(selectedProcessTemplateOption);
        }
        return result;
    }, [selectedProcessTemplateOption], "Get Latest Published Process Template Summary");
    const [isUpdateVersionCalloutDismissed, setUpdateVersionCalloutDismissed] = useLocalStorage(`Octopus.Callout.ProcessTemplate.UpdateVersion.${action.Name}.Dismissed`, false);
    const latestStableVersion = latestProcessTemplateVersionSummary?.LatestStableVersion;
    const selectedProcessTemplateVersion = selectedProcessTemplateDetails?.processTemplateVersion;
    const showSnackbar = useShowSnackbar();
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(mapMaskToUsageConstraint(processTemplateVersionMask));
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(mapMaskToUpdateBehaviour(processTemplateVersionMask));
    const onUpdateToLatestVersion = () => {
        if (latestStableVersion) {
            if (selectedUsageConstraint === UsageConstraint.PreRelease) {
                setSelectedUsageConstraint(UsageConstraint.Stable);
                setSelectedUpdateBehaviour(UpdateBehaviour.All);
                const versionMask = getVersionMask(latestProcessTemplateVersionSummary?.LatestStableVersion, UpdateBehaviour.All);
                setVersionMaskProperty(versionMask);
            }
            else {
                const versionMask = getVersionMask(latestProcessTemplateVersionSummary?.LatestStableVersion, selectedUpdateBehaviour);
                setVersionMaskProperty(versionMask);
            }
            setInitialProcessTemplateVersion(latestProcessTemplateVersionSummary?.LatestStableVersion);
            showSnackbar("Process template updated to latest version.");
        }
    };
    return (<>
            <ExpandableContainer containerKey={action.Id}>
                <UpdateProcessTemplateVersionCallout onUpdateToLatest={onUpdateToLatestVersion} onDismiss={() => setUpdateVersionCalloutDismissed(true)} isDismissed={isUpdateVersionCalloutDismissed} currentVersion={selectedProcessTemplateVersion} latestStableVersion={latestStableVersion}/>
                <ActionLayout step={step} action={action} stepOther={stepOther} setDisabled={setDisabled} releaseCreationPackageStepId={releaseCreationPackageStepId}>
                    <>
                        <ActionDisabledWarning action={action}/>
                        <ErrorsForAction actionErrors={actionErrors}/>
                        {distinctTemplates ? (<>
                                <ExpandableFormSection isExpandedByDefault={isNew} errorKey="Step Name" title="Step Name" summary={buildNameSummary(action)} key="Step Name" help="A short, memorable, unique name for this step.">
                                    <DebounceText value={action.Name} onChange={setName} label="Step name" error={getFieldError("Name")} validate={required("Please enter a step name")} autoFocus={true}/>
                                    {!isNew && (<SlugEditor value={action.Slug ?? generateSlug(action.Name)} name={action.Name} originalSlug={cleanAction?.Slug ?? ""} onChange={setSlug} label="Step slug" error={getFieldError("Slug")} validate={required("Please enter a step slug")}/>)}
                                </ExpandableFormSection>

                                <ProcessTemplateVersionSelector initialActionProperties={initialActionProperties} initialProcessTemplateVersion={initialProcessTemplateVersion} actionProperties={action.Properties} isNew={isNew} selectedProcessTemplateDetails={selectedProcessTemplateDetails} setProcessTemplateSlugProperty={setProcessTemplateSlugProperty} distinctTemplates={distinctTemplates} setVersionMaskProperty={setVersionMaskProperty} latestProcessTemplateVersionSummary={latestProcessTemplateVersionSummary} selectedProcessTemplateOption={selectedProcessTemplateOption} setSelectedProcessTemplateOption={setSelectedProcessTemplateOption} selectedUpdateBehaviour={selectedUpdateBehaviour} setSelectedUpdateBehaviour={setSelectedUpdateBehaviour} selectedUsageConstraint={selectedUsageConstraint} setSelectedUsageConstraint={setSelectedUsageConstraint}/>

                                {selectedProcessTemplate ? (<BlueprintActionDetails stepId={step.Id} action={action} doBusyTask={doBusyTask} onParameterValueChanged={setParameterValue} blueprint={selectedProcessTemplate} gitRefResource={gitRefResource} libraryVariableSets={libraryVariableSets} processVariableSet={processVariableSet} environments={environments}/>) : null}
                            </>) : null}
                    </>
                </ActionLayout>
            </ExpandableContainer>
        </>);
}
interface ActionLayoutProps {
    step: StoredStep;
    action: StoredAction;
    stepOther: ProcessStepActionState;
    setDisabled: (value: boolean) => void;
    children: JSX.Element;
    releaseCreationPackageStepId: string | undefined;
}
function ActionLayout({ step, action, setDisabled, stepOther, releaseCreationPackageStepId, children }: ActionLayoutProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const { actions: contextActions, selectors: contextSelectors } = processContext;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const stepActionNumber = `${contextSelectors.getStepNumber(step.Id)}`;
    const title = <StepName name={action.Name} number={stepActionNumber} stepType={stepOther.actionTypeName}/>;
    const logo = <Logo url={getActionLogoUrl(action)}/>;
    const menuItems: MenuItem[] = [];
    if (step && step.Id) {
        const processEditPermission = {
            permission: processScopedEditPermission(processContext.selectors.getProcessType()),
            project: isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined,
            wildcard: true,
        };
        const enableItem = OverflowMenuItems.item(action.IsDisabled ? "Enable" : "Disable", () => setDisabled(!action.IsDisabled), processEditPermission);
        const deleteItem = getDeleteProcessMenuItem("step", async () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, releaseCreationPackageStepId, processIdentifier, step, action, processContext);
        menuItems.push(enableItem, deleteItem);
    }
    const controlActions = [<OverflowMenu menuItems={menuItems} accessibleName="More Step Actions"/>];
    const sectionControl = <ActionList actions={controlActions}/>;
    return (<ProcessSubPageLayout title={title} titleLogo={logo} sectionControl={sectionControl}>
            {children}
        </ProcessSubPageLayout>);
}
function buildNameSummary(action: StoredAction) {
    if (!action.Name) {
        return Summary.placeholder("Please enter a name for your step");
    }
    const name = (<div className={styles.nameSummaryContainer}>
            <NameSummaryWithSlug name={action.Name} slug={action.Slug || null}/>
        </div>);
    return Summary.summary(name);
}
interface ActionDisabledWarningProps {
    action: StoredAction;
}
function ActionDisabledWarning({ action }: ActionDisabledWarningProps) {
    if (!action.IsDisabled) {
        return null;
    }
    return (<UnstructuredFormSection stretchContent={true}>
            <Callout type={"warning"} title={"This step is currently disabled"}/>
        </UnstructuredFormSection>);
}
